// The full filters panel for the "הוספת נכסים" sub-tab.
//
// This is NOT a second filter panel — it is the listings page's own
// listingsMatch/ListingsFiltersDrawer, driven through its `contextOverride` prop so it
// runs without the ListingsMatchContext god-context (the same escape hatch listingsBoard
// and rentalToSale already use). This file supplies only the three things that override
// needs: a draft SearchState, a setter, and a commit. All shape translation lives in
// matchFiltersAdapter.ts.
//
// Draft-vs-live semantics, deliberately different from the chip row:
// - The chips apply instantly (one control, one change, one search).
// - The drawer is ~15 controls, so it keeps the listings page's batch model: edits go to
//   a local draft and only the drawer's own "חיפוש" button commits them upward, which
//   fires exactly one search instead of one per keystroke.
// The draft is re-seeded from the live filters on every OPEN, so edits made in the chip
// row while the drawer was closed are always reflected — the two surfaces cannot drift.
import { useEffect, useState } from "react";
import { propertyTypes as allPropertyTypes } from "../../../../../../@types/sharedSchema";
import { ListingsFiltersDrawer } from "components/_dashboard/listingsMatch/ListingsFiltersDrawer";
import type { SearchState } from "contexts/ListingsMatchContext";
import type { MatchFilters } from "./contactMatchFilters";
import {
  CRM_FROZEN_FILTERS,
  matchFiltersToSearchState,
  drawerDraftToFiltersPatch
} from "./matchFiltersAdapter";

// The canonical taxonomy — the same list the chip row's סוג נכס editor offers, and the
// exact domain of ProcessedListingData.propertyType. The listings page instead feeds the
// drawer an Algolia facet list; here the static list is both cheaper (no extra query per
// mount) and safer, because a type picked in the chips can never be missing from the
// drawer's dropdown.
const CRM_PROPERTY_TYPES: string[] = [...allPropertyTypes];

type MatchesFiltersDrawerProps = {
  open: boolean;
  /** Live filter state — the draft is re-seeded from this on every open. */
  filters: MatchFilters;
  onClose: () => void;
  /** Commit: fired once, when the realtor presses the drawer's search button. */
  onApply: (patch: Partial<MatchFilters>) => void;
};

export default function MatchesFiltersDrawer({
  open,
  filters,
  onClose,
  onApply
}: MatchesFiltersDrawerProps): JSX.Element {
  const [draft, setDraft] = useState<SearchState>(() => matchFiltersToSearchState(filters));

  // Re-seed on open only. Re-seeding while OPEN would throw away edits in progress every
  // time the underlying search state changed (e.g. the live contact snapshot re-seeds the
  // filters), so `filters` is deliberately not a dependency here.
  useEffect(() => {
    if (!open) return;
    setDraft(matchFiltersToSearchState(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ListingsFiltersDrawer
      isDrawerOpen={open}
      onCloseRequest={onClose}
      frozenFilters={CRM_FROZEN_FILTERS}
      contextOverride={{
        draftSearchState: draft,
        availablePropertyTypes: CRM_PROPERTY_TYPES,
        // The drawer calls this with partials (and once with a full spread) — merge both.
        setDraftSearchState: (patch) => setDraft((prev) => ({ ...prev, ...patch })),
        // The drawer closes itself right after this returns.
        applyDraftSearchState: () => onApply(drawerDraftToFiltersPatch(draft, filters))
      }}
    />
  );
}
